import * as React from 'react';

interface PresentationModeProviderProps {
  children: React.ReactNode;
  defaultEnabled?: boolean;
  storageKey?: string;
}

interface PresentationModeProviderState {
  presentationMode: boolean;
  setPresentationMode: (enabled: boolean) => void;
  togglePresentationMode: () => void;
}

const PresentationModeContext = React.createContext<
  PresentationModeProviderState | undefined
>(undefined);

function readStoredPresentationMode(
  storageKey: string,
  defaultEnabled: boolean
) {
  if (typeof window === 'undefined') return defaultEnabled;

  try {
    const stored = window.localStorage.getItem(storageKey);
    if (stored === null) return defaultEnabled;
    return stored === 'true';
  } catch {
    return defaultEnabled;
  }
}

export function PresentationModeProvider({
  children,
  defaultEnabled = false,
  storageKey = 'dataroom-presentation-mode:v1'
}: PresentationModeProviderProps) {
  const [presentationMode, setPresentationModeState] = React.useState(() =>
    readStoredPresentationMode(storageKey, defaultEnabled)
  );

  React.useEffect(() => {
    setPresentationModeState(
      readStoredPresentationMode(storageKey, defaultEnabled)
    );
  }, [defaultEnabled, storageKey]);

  React.useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== storageKey) return;
      setPresentationModeState(
        readStoredPresentationMode(storageKey, defaultEnabled)
      );
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [defaultEnabled, storageKey]);

  const setPresentationMode = React.useCallback(
    (enabled: boolean) => {
      try {
        window.localStorage.setItem(storageKey, String(enabled));
      } catch {
        // Presentation mode is local UI state; keep it working if storage is unavailable.
      }
      setPresentationModeState(enabled);
    },
    [storageKey]
  );

  const togglePresentationMode = React.useCallback(() => {
    setPresentationModeState((current) => {
      const next = !current;
      try {
        window.localStorage.setItem(storageKey, String(next));
      } catch {
        // Keep the in-memory toggle responsive even if storage is unavailable.
      }
      return next;
    });
  }, [storageKey]);

  const value = React.useMemo(
    () => ({
      presentationMode,
      setPresentationMode,
      togglePresentationMode
    }),
    [presentationMode, setPresentationMode, togglePresentationMode]
  );

  return (
    <PresentationModeContext.Provider value={value}>
      {children}
    </PresentationModeContext.Provider>
  );
}

export function usePresentationMode() {
  const context = React.useContext(PresentationModeContext);
  if (!context) {
    throw new Error(
      'usePresentationMode must be used within a PresentationModeProvider'
    );
  }
  return context;
}
